import { useQuery } from "@tanstack/react-query";
import { Phone, Mail, User, Crown } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Spinner } from "@/components/ui";

// =============================================================================
// CrewRosterPanel — T-148. A small read-only roster card for a single crew.
//
// Used inline beneath:
//   • each crew assignment row on ProjectCrewAssignmentsCard (project detail)
//   • each crew assignment row on ProjectSchedulePanel (schedule drawer)
//
// Pulls active crew_members joined to employees so on-site supervisors can
// see who's on the team and tap to ring them. Lead-of-the-crew gets a crown.
// Past members (member_to in the past) are hidden — keep it about "right now".
// =============================================================================

const CREW_ROLE_LABELS: Record<string, string> = {
  lead_rigger:     "Lead rigger",
  offsider_rigger: "Offsider",
  tech:            "Tech",
  rigger:          "Rigger",
  driver:          "Driver",
  apprentice:      "Apprentice",
  other:           "Other",
};

interface RosterRow {
  crew_member_id: string;
  is_lead:        boolean;
  crew_role:      string;
  member_from:    string;
  member_to:      string | null;
  employees: {
    employee_id:        string;
    first_name:         string;
    last_name:          string;
    mobile:             string | null;
    email:              string | null;
    primary_role_name:  string | null;
  } | null;
}

export interface Props {
  crewId:  string;
  /** Tighter padding + fewer details for narrow side-drawer use. */
  compact?: boolean;
}

export default function CrewRosterPanel({ crewId, compact = false }: Props) {
  const { data: roster, isLoading } = useQuery({
    queryKey: ["crew_roster", crewId],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("crew_members")
        .select(`
          crew_member_id, is_lead, crew_role, member_from, member_to,
          employees:employee_id (
            employee_id, first_name, last_name, mobile, email, primary_role_name
          )
        `)
        .eq("crew_id", crewId)
        // current members only — open-ended (member_to null) OR ends today/later
        .or(`member_to.is.null,member_to.gte.${today}`)
        .order("is_lead", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as RosterRow[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-2">
        <Spinner size={12}/>
      </div>
    );
  }

  if (!roster || roster.length === 0) {
    return (
      <div className="text-[11px] text-text-muted italic px-2 py-1.5">
        No current members. Add some on the Crews admin screen.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {roster.map(m => {
        const e = m.employees;
        if (!e) return null;
        const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || "—";
        const roleLbl = CREW_ROLE_LABELS[m.crew_role] ?? m.crew_role;
        // Strip whitespace from phone for the tel: link, but keep it for display.
        const telHref = e.mobile ? `tel:${e.mobile.replace(/[^\d+]/g, "")}` : null;

        return (
          <div
            key={m.crew_member_id}
            className={`flex items-center justify-between gap-2 bg-ink/60 rounded border border-line/40 ${
              compact ? "px-2 py-1" : "px-2.5 py-1.5"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {m.is_lead || m.crew_role === "lead_rigger" ? (
                <Crown size={11} className="text-warn flex-shrink-0" aria-label="Crew lead"/>
              ) : (
                <User size={11} className="text-text-dim flex-shrink-0"/>
              )}
              <div className="min-w-0">
                <div className={`${compact ? "text-[11px]" : "text-[12px]"} font-medium truncate`}>
                  {name}
                </div>
                <div className="text-[10px] text-text-muted truncate">
                  {roleLbl}
                  {!compact && e.primary_role_name && e.primary_role_name !== roleLbl && (
                    <span className="ml-1 text-text-dim">· {e.primary_role_name}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {telHref ? (
                <a
                  href={telHref}
                  className="inline-flex items-center gap-1 text-text-muted hover:text-accent"
                  title={`Call ${name}`}
                >
                  <Phone size={10}/>
                  <span className="font-mono text-[10px] hidden md:inline">{e.mobile}</span>
                </a>
              ) : (
                <span className="text-text-dim text-[10px] inline-flex items-center gap-1" title="No mobile on file">
                  <Phone size={10}/>
                </span>
              )}
              {e.email && (
                <a
                  href={`mailto:${e.email}`}
                  className="text-text-muted hover:text-accent"
                  title={`Email ${name}`}
                >
                  <Mail size={10}/>
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
